/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { t } from '@sportninja/common/i18n'

import { Flex } from '../Layout'
import DatePicker from '../DatePicker'
import { inputCss, Label, InputWrapper } from '../Form/css'
import { toISOString } from '../../utils/utils'

export const getDurationInMinutes = (start, end) => {
  const diff = dayjs(end).diff(dayjs(start), 'minute')
  return diff
}

const DateAndDurationPicker = ({
  errors = {},
  datePlaceholder,
  disabled,
  defaultValues = {},
  onChange,
  ...props
}) => {
  const firstUpdate = useRef(true)
  const firstUpdate2 = useRef(true)
  const { starts_at = {}, ends_at = {} } = defaultValues
  const { time_to_be_defined_status } = props?.values?.time_to_be_defined_status
    ? props.values
    : props

  const [start, setStart] = useState(
    starts_at.value ? new Date(starts_at.value) : undefined
  )
  const [duration, setDuration] = useState(
    ends_at.value ? getDurationInMinutes(start, ends_at.value) : ''
  )

  const handleChange = (method, { target }) => method(target.value)

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    let end
    if (time_to_be_defined_status && start) {
      end = dayjs(start).utc().endOf('day').add(duration, 'minute')
    } else {
      // Keep existing behavior when time_to_be_defined_status is false
      end = dayjs(start).add(duration, 'minute')
    }
    if (start) {
      onChange({
        target: {
          name: 'starts_at',
          value: toISOString(dayjs(start)),
        },
      })
    }
    if (end) {
      onChange({
        target: {
          name: 'ends_at',
          value: toISOString(end),
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, duration, time_to_be_defined_status])

  const sharedDurationProps = {
    disabled,
    required: ends_at.required,
    hasError: !!errors.ends_at,
  }

  const handleTimeTDBChange = useCallback(
    (checked) => {
      // When switching OFF (false), set time to current time while keeping the date
      if (!checked) {
        const now = dayjs()
        const updatedStart = start
          ? dayjs(start)
              .hour(now.hour())
              .minute(now.minute())
              .second(now.second())
              .millisecond(now.millisecond())
              .toDate()
          : now.toDate()
        setStart(toISOString(dayjs(updatedStart)))
        onChange({
          target: {
            name: 'starts_at',
            value: toISOString(dayjs(updatedStart)),
          },
        })
      }
      onChange({
        target: {
          name: 'time_to_be_defined_status',
          value: checked,
        },
      })
    },
    [onChange, start]
  )

  const handleDurationChange = useCallback((value) => {
    setDuration(value)
    onChange({
      target: {
        name: 'duration',
        value: value,
      },
    })
  }, [])

  useEffect(() => {
    if (firstUpdate2.current) {
      firstUpdate2.current = false
      return
    }
    handleTimeTDBChange(time_to_be_defined_status)
  }, [time_to_be_defined_status])

  return (
    <Fragment>
      <DatePicker
        datePlaceholder={datePlaceholder}
        defaultValue={start}
        disabled={disabled}
        hasError={!!errors.starts_at}
        name='starts_at'
        label={starts_at.label}
        onChange={handleChange.bind(this, setStart)}
        required={starts_at.required}
        timezone={starts_at.timezone}
        time_to_be_defined_status={time_to_be_defined_status}
        showTimeTDBToggle={props.showTimeTDBToggle}
        onTimeTDBChange={handleTimeTDBChange}
      />
      <InputWrapper {...sharedDurationProps}>
        <Label {...sharedDurationProps}>
          {ends_at.label}
          {ends_at.required ? ' *' : ''}
        </Label>
        <Flex>
          <input
            css={[
              inputCss(sharedDurationProps),
              css`
                padding-right: 88px;
              `,
            ]}
            disabled={disabled}
            min='0'
            // onChange={handleChange.bind(this, setDuration)}
            onChange={(e) => handleDurationChange(e.target.value)}
            required={ends_at.required}
            step='1'
            style={{ width: 'calc(100% + 100px)' }}
            type='number'
            value={duration}
          />
          <div
            css={css`
              position: absolute;
              top: calc(50% + 3px);
              right: 16px;
            `}
          >
            {t('common:minutes')}
          </div>
        </Flex>
      </InputWrapper>
    </Fragment>
  )
}

DateAndDurationPicker.propTypes = {
  errors: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  datePlaceholder: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  showTimeTDBToggle: PropTypes.bool,
  onTimeTDBChange: PropTypes.func,
}

export default DateAndDurationPicker
